import { Vector2, Vector3, TRSTransform, Quad } from './utils/transform';

export enum Environment
{
	// Reserved: 0
	Desktop = 1,
	Overlay = 2,

	Unknown = 100,
}

export function CurrentEnvironment(): Environment
{
	// TODO(joe): Make this function also return the correct value for steamwebhelper overlays.

	return VRHTML?.Environment() ?? Environment.Unknown;
}

export function OpenSteamWorkshop( unSteamAppID: number )
{
	if ( VRHTML )
	{
		VRHTML.OpenSteamWorkshop( unSteamAppID );
	}
	else
	{
		window.open( "https://steamcommunity.com/app/" + unSteamAppID + "/workshop" );
	}
}

// If this page is accessed outside of CEF, create a global falsy VRHTML value.
if ( !( window as any ).hasOwnProperty( "VRHTML" ) )
{
	( window as any ).VRHTML = null;
}

export enum EOverlayRenderQuality
{
	Auto = 0,
	Low = 1,
	Medium = 2,
	High = 3,
}

export enum ETrackingResult {
	TrackingResult_Uninitialized			= 1,

	TrackingResult_Calibrating_InProgress	= 100,
	TrackingResult_Calibrating_OutOfRange	= 101,

	TrackingResult_Running_OK				= 200,
	TrackingResult_Running_OutOfRange		= 201,

	TrackingResult_Fallback_RotationOnly	= 300,
}

export enum ETrackingUniverseOrigin
{
	Seated = 0,		// Poses are provided relative to the seated zero pose
	Standing = 1,	// Poses are provided relative to the safe bounds configured by the user
	RawAndUncalibrated = 2,	// Poses are provided in the coordinate system defined by the driver.  It has Y up and is unified for devices of the same driver. You usually don't want this one.
}

export interface CallbackHandle {
	unregister: () => void;
}

export interface TrackedDevicePose {
	xfDeviceToAbsoluteTracking: TRSTransform;
	vVelocity: Vector3;
	eTrackingResult: ETrackingResult;
	bPoseIsValid: boolean;
}

export interface DashboardOverlay {
	name ?: string;
	key ?: string;
	thumbnailKey ?: string;
	widthInMeters ?: number;
	noDashboardTab ?: boolean;
}

export let k_ulOverlayHandleInvalid: string = "0";

export let k_unTrackedDeviceIndex_Hmd: number = 0;
export let k_unTrackedDeviceIndexInvalid: number = -1;


export enum ESDLWindowEvent
{
	None,        // Never used
	Shown,       // Window has been shown
	Hidden,      // Window has been hidden
	Exposed,     // Window has been exposed and should be redrawn
	Moved,       // Window has been moved to data1, data2
	Resized,     // Window has been resized to data1xdata2
	SizeChanged, // The window size has changed, either as a result of an API call or through the system or user changing the window size.
	Minimized,   // Window has been minimized
	Maximized,   // Window has been maximized
	Restored,    // Window has been restored to normal size and position
	Enter,       // Window has gained mouse focus
	Leave,       // Window has lost mouse focus
	FocusGained, // Window has gained keyboard focus
	FocusLost,   // Window has lost keyboard focus
	Close,       // The window manager requests that the window be closed
	TakeFocus,   // Window is being offered a focus (should SetWindowInputFocus() on itself or a subwindow, or ignore)
	HitTest,     // Window had a hit test that wasn't SDL_HITTEST_NORMAL.
}

export enum EDeviceEvent
{
	Activated,
	Deactivated,
}

export enum EVROverlayFlags
{
	NoDashboardTab = 1 << 3,
	AcceptsGamepadEvents = 1 << 4,
	ShowGamepadFocus = 1 << 5,
	SendVRDiscreteScrollEvents = 1 << 6,
	SendVRTouchpadEvents = 1 << 7,
	ShowTouchPadScrollWheel = 1 << 8,
	TransferOwnershipToInternalProcess = 1 << 9,
	SideBySide_Parallel = 1 << 10, // Texture is left/right
	SideBySide_Crossed = 1 << 11, // Texture is crossed and right/left
	Panorama = 1 << 12, // Texture is a panorama
	StereoPanorama = 1 << 13, // Texture is a stereo panorama
	SortWithNonSceneOverlays = 1 << 14,
	VisibleInDashboard = 1 << 15,
	MakeOverlaysInteractiveIfVisible = 1 << 16,
	SendVRSmoothScrollEvents = 1 << 17,
	ProtectedContent = 1 << 18,
	HideLaserIntersection = 1 << 19,
	WantsModalBehavior = 1 << 20,
}

export enum EVROverlayInputMethod
{
	None		= 0, // No input events will be generated automatically for this overlay
	Mouse		= 1, // Tracked controllers will get mouse events automatically
	// DualAnalog = 2, // Analog inputs from tracked controllers are turned into DualAnalog events
}

export type VROverlayHandle_t = string;

export enum ETrackedDeviceProperty
{
	Invalid								= 0,

	// general properties that apply to all device classes
	TrackingSystemName_String				= 1000,
	ModelNumber_String						= 1001,
	SerialNumber_String					= 1002,
	RenderModelName_String					= 1003,
	WillDriftInYaw_Bool					= 1004,
	ManufacturerName_String				= 1005,
	TrackingFirmwareVersion_String			= 1006,
	HardwareRevision_String				= 1007,
	AllWirelessDongleDescriptions_String	= 1008,
	ConnectedWirelessDongle_String			= 1009,
	DeviceIsWireless_Bool					= 1010,
	DeviceIsCharging_Bool					= 1011,
	DeviceBatteryPercentage_Float			= 1012, // 0 is empty, 1 is full
	StatusDisplayTransform_Matrix34		= 1013,
	Firmware_UpdateAvailable_Bool			= 1014,
	Firmware_ManualUpdate_Bool				= 1015,
	Firmware_ManualUpdateURL_String		= 1016,
	HardwareRevision_Uint64				= 1017,
	FirmwareVersion_Uint64					= 1018,
	FPGAVersion_Uint64						= 1019,
	VRCVersion_Uint64						= 1020,
	RadioVersion_Uint64					= 1021,
	DongleVersion_Uint64					= 1022,
	BlockServerShutdown_Bool				= 1023,
	CanUnifyCoordinateSystemWithHmd_Bool	= 1024,
	ContainsProximitySensor_Bool			= 1025,
	DeviceProvidesBatteryStatus_Bool		= 1026,
	DeviceCanPowerOff_Bool					= 1027,
	Firmware_ProgrammingTarget_String		= 1028,
	DeviceClass_Int32						= 1029,
	HasCamera_Bool							= 1030,
	DriverVersion_String                   = 1031,
	Firmware_ForceUpdateRequired_Bool      = 1032,
	ViveSystemButtonFixRequired_Bool		= 1033,
	ParentDriver_Uint64					= 1034,
	ResourceRoot_String					= 1035,
	RegisteredDeviceType_String			= 1036,
	InputProfilePath_String				= 1037, // input profile to use for this device in the input system. Will default to tracking system name if this isn't provided
	NeverTracked_Bool						= 1038, // Used for devices that will never have a valid pose by design
	NumCameras_Int32						= 1039,
	CameraFrameLayout_Int32				= 1040, // EVRTrackedCameraFrameLayout value
	CameraStreamFormat_Int32				= 1041, // ECameraVideoStreamFormat value
	AdditionalDeviceSettingsPath_String	= 1042, // driver-relative path to additional device and global configuration settings

	// Properties that are unique to TrackedDeviceClass_HMD
	ReportsTimeSinceVSync_Bool				= 2000,
	SecondsFromVsyncToPhotons_Float		= 2001,
	DisplayFrequency_Float					= 2002,
	UserIpdMeters_Float					= 2003,
	CurrentUniverseId_Uint64				= 2004,
	PreviousUniverseId_Uint64				= 2005,
	DisplayFirmwareVersion_Uint64			= 2006,
	IsOnDesktop_Bool						= 2007,
	DisplayMCType_Int32					= 2008,
	DisplayMCOffset_Float					= 2009,
	DisplayMCScale_Float					= 2010,
	EdidVendorID_Int32						= 2011,
	DisplayMCImageLeft_String              = 2012,
	DisplayMCImageRight_String             = 2013,
	DisplayGCBlackClamp_Float				= 2014,
	EdidProductID_Int32					= 2015,
	CameraToHeadTransform_Matrix34			= 2016,
	DisplayGCType_Int32					= 2017,
	DisplayGCOffset_Float					= 2018,
	DisplayGCScale_Float					= 2019,
	DisplayGCPrescale_Float				= 2020,
	DisplayGCImage_String					= 2021,
	LensCenterLeftU_Float					= 2022,
	LensCenterLeftV_Float					= 2023,
	LensCenterRightU_Float					= 2024,
	LensCenterRightV_Float					= 2025,
	UserHeadToEyeDepthMeters_Float			= 2026,
	CameraFirmwareVersion_Uint64			= 2027,
	CameraFirmwareDescription_String		= 2028,
	DisplayFPGAVersion_Uint64				= 2029,
	DisplayBootloaderVersion_Uint64		= 2030,
	DisplayHardwareVersion_Uint64			= 2031,
	AudioFirmwareVersion_Uint64			= 2032,
	CameraCompatibilityMode_Int32			= 2033,
	ScreenshotHorizontalFieldOfViewDegrees_Float = 2034,
	ScreenshotVerticalFieldOfViewDegrees_Float = 2035,
	DisplaySuppressed_Bool					= 2036,
	DisplayAllowNightMode_Bool				= 2037,
	DisplayMCImageWidth_Int32				= 2038,
	DisplayMCImageHeight_Int32				= 2039,
	DisplayMCImageNumChannels_Int32		= 2040,
	DisplayMCImageData_Binary				= 2041,
	SecondsFromPhotonsToVblank_Float		= 2042,
	DriverDirectModeSendsVsyncEvents_Bool	= 2043,
	DisplayDebugMode_Bool					= 2044,
	GraphicsAdapterLuid_Uint64				= 2045,
	DriverProvidedChaperonePath_String		= 2048,
	ExpectedTrackingReferenceCount_Int32	= 2049, // expected number of sensors or basestations to reserve UI space for
	ExpectedControllerCount_Int32			= 2050, // expected number of tracked controllers to reserve UI space for
	NamedIconPathControllerLeftDeviceOff_String	= 2051, // placeholder icon for "left" controller if not yet detected/loaded
	NamedIconPathControllerRightDeviceOff_String	= 2052, // placeholder icon for "right" controller if not yet detected/loaded
	NamedIconPathTrackingReferenceDeviceOff_String	= 2053, // placeholder icon for sensor/base if not yet detected/loaded
	DoNotApplyPrediction_Bool				= 2054,
	CameraToHeadTransforms_Matrix34_Array	= 2055,
	DistortionMeshResolution_Int32			= 2056, // custom resolution of compositor calls to IVRSystem::ComputeDistortion
	DriverIsDrawingControllers_Bool		= 2057,
	DriverRequestsApplicationPause_Bool	= 2058,
	DriverRequestsReducedRendering_Bool	= 2059,
	MinimumIpdStepMeters_Float				= 2060,
	AudioBridgeFirmwareVersion_Uint64		= 2061,
	ImageBridgeFirmwareVersion_Uint64		= 2062,
	ImuToHeadTransform_Matrix34			= 2063,
	ImuFactoryGyroBias_Vector3				= 2064,
	ImuFactoryGyroScale_Vector3			= 2065,
	ImuFactoryAccelerometerBias_Vector3	= 2066,
	ImuFactoryAccelerometerScale_Vector3	= 2067,
	// reserved 2068
	ConfigurationIncludesLighthouse20Features_Bool = 2069,
	Prop_AdditionalRadioFeatures_Uint64         = 2070,
	Prop_CameraWhiteBalance_Vector4_Array		= 2071, // Prop_NumCameras_Int32-sized array of float[4] RGBG white balance calibration data (max size is vr::k_unMaxCameras)
	Prop_CameraDistortionFunction_Int32_Array	= 2072, // Prop_NumCameras_Int32-sized array of vr::EVRDistortionFunctionType values (max size is vr::k_unMaxCameras)
	Prop_CameraDistortionCoefficients_Float_Array = 2073, // Prop_NumCameras_Int32-sized array of double[vr::k_unMaxDistortionFunctionParameters] (max size is vr::k_unMaxCameras)
	Prop_ExpectedControllerType_String			= 2074,
	HmdTrackingStyle_Int32					= 2075, // one of EHmdTrackingStyle
	DriverProvidedChaperoneVisibility_Bool = 2076,
	HmdColumnCorrectionSettingPrefix_String		= 2077,
	CameraSupportsCompatibilityModes_Bool	= 2078,
	SupportsRoomViewDepthProjection_Bool	= 2079,

	DisplayAvailableFrameRates_Float_Array = 2080,
	DisplaySupportsRuntimeFramerateChange_Bool = 2084,
	DisplaySupportsAnalogGain_Bool = 2085,
	DisplayMinAnalogGain_Float = 2086,
	DisplayMaxAnalogGain_Float = 2087,

	DashboardScale_Float 					= 2091,
	IpdUIRangeMinMeters_Float 				= 2100,
	IpdUIRangeMaxMeters_Float 				= 2101,
	Hmd_SupportsHDCP14LegacyCompat_Bool		= 2102,
	Hmd_SupportsMicMonitoring_Bool			= 2103,
	Audio_SupportsDualSpeakerAndJackOutput_Bool	= 2303,

	// Driver requested mura correction properties
	DriverRequestedMuraCorrectionMode_Int32		= 2200,
	DriverRequestedMuraFeather_InnerLeft_Int32		= 2201,
	DriverRequestedMuraFeather_InnerRight_Int32	= 2202,
	DriverRequestedMuraFeather_InnerTop_Int32		= 2203,
	DriverRequestedMuraFeather_InnerBottom_Int32	= 2204,
	DriverRequestedMuraFeather_OuterLeft_Int32		= 2205,
	DriverRequestedMuraFeather_OuterRight_Int32	= 2206,
	DriverRequestedMuraFeather_OuterTop_Int32		= 2207,
	DriverRequestedMuraFeather_OuterBottom_Int32	= 2208,

	// Properties that are unique to TrackedDeviceClass_Controller
	AttachedDeviceId_String				= 3000,
	SupportedButtons_Uint64				= 3001,
	Axis0Type_Int32						= 3002, // Return value is of type EVRControllerAxisType
	Axis1Type_Int32						= 3003, // Return value is of type EVRControllerAxisType
	Axis2Type_Int32						= 3004, // Return value is of type EVRControllerAxisType
	Axis3Type_Int32						= 3005, // Return value is of type EVRControllerAxisType
	Axis4Type_Int32						= 3006, // Return value is of type EVRControllerAxisType
	ControllerRoleHint_Int32				= 3007, // Return value is of type ETrackedControllerRole

	// Properties that are unique to TrackedDeviceClass_TrackingReference
	FieldOfViewLeftDegrees_Float			= 4000,
	FieldOfViewRightDegrees_Float			= 4001,
	FieldOfViewTopDegrees_Float			= 4002,
	FieldOfViewBottomDegrees_Float			= 4003,
	TrackingRangeMinimumMeters_Float		= 4004,
	TrackingRangeMaximumMeters_Float		= 4005,
	ModeLabel_String						= 4006,

	// Properties that are used for user interface like icons names
	IconPathName_String						= 5000, // DEPRECATED. Value not referenced. Now expected to be part of icon path properties.
	NamedIconPathDeviceOff_String				= 5001, // {driver}/icons/icon_filename - PNG for static icon, or GIF for animation, 50x32 for headsets and 32x32 for others
	NamedIconPathDeviceSearching_String		= 5002, // {driver}/icons/icon_filename - PNG for static icon, or GIF for animation, 50x32 for headsets and 32x32 for others
	NamedIconPathDeviceSearchingAlert_String	= 5003, // {driver}/icons/icon_filename - PNG for static icon, or GIF for animation, 50x32 for headsets and 32x32 for others
	NamedIconPathDeviceReady_String			= 5004, // {driver}/icons/icon_filename - PNG for static icon, or GIF for animation, 50x32 for headsets and 32x32 for others
	NamedIconPathDeviceReadyAlert_String		= 5005, // {driver}/icons/icon_filename - PNG for static icon, or GIF for animation, 50x32 for headsets and 32x32 for others
	NamedIconPathDeviceNotReady_String			= 5006, // {driver}/icons/icon_filename - PNG for static icon, or GIF for animation, 50x32 for headsets and 32x32 for others
	NamedIconPathDeviceStandby_String			= 5007, // {driver}/icons/icon_filename - PNG for static icon, or GIF for animation, 50x32 for headsets and 32x32 for others
	NamedIconPathDeviceAlertLow_String			= 5008, // {driver}/icons/icon_filename - PNG for static icon, or GIF for animation, 50x32 for headsets and 32x32 for others

	// Properties that are used by helpers, but are opaque to applications
	DisplayHiddenArea_Binary_Start				= 5100,
	DisplayHiddenArea_Binary_End				= 5150,
	ParentContainer							= 5151,

	// Properties that are unique to drivers
	UserConfigPath_String					= 6000,
	InstallPath_String						= 6001,
	HasDisplayComponent_Bool				= 6002,
	HasControllerComponent_Bool			= 6003,
	HasCameraComponent_Bool				= 6004,
	HasDriverDirectModeComponent_Bool		= 6005,
	HasVirtualDisplayComponent_Bool		= 6006,
	HasSpatialAnchorsSupport_Bool		    = 6007,

	// Properties that are set internally based on other information provided by drivers
	ControllerType_String					= 7000,
	LegacyInputProfile_String				= 7001,

	// Vendors are free to expose private debug data in this reserved region
	VendorSpecific_Reserved_Start			= 10000,
	VendorSpecific_Reserved_End			= 10999,

	TrackedDeviceProperty_Max				= 1000000,
}

export interface RenderModel_ComponentState_t
{
	xfTrackingToComponentRenderModel: TRSTransform;  // Transform required when drawing the component render model
	xfTrackingToComponentLocal: TRSTransform;        // Transform available for attaching to a local component coordinate system (-Z out from surface )
}

export enum ETrackedDeviceClass
{
	Invalid = 0,				// the ID was not valid.
	HMD = 1,					// Head-Mounted Displays
	Controller = 2,			// Tracked controllers
	GenericTracker = 3,		// Generic trackers, similar to controllers
	TrackingReference = 4,	// Camera and base stations that serve as tracking reference points
	DisplayRedirect = 5,		// Accessories that aren't necessarily tracked themselves, but may redirect video output from other tracked devices
}

export interface TrackedDeviceInfo
{
	unIndex: number;
	eClass: ETrackedDeviceClass;
	sDevicePath?: string;
}

export enum GraphicsHardwareVendor
{
	Unknown = 0,
	NVIDIA = 1,
	AMD = 2,
}

export interface GraphicsHardwareVendorInfo
{
	eVendor: GraphicsHardwareVendor;
	sVersion: string;
	bSupportsDirectMode: boolean;
	sDirectModeRequiredVersion: string;
	bSupportsMotionSmoothing: boolean;
	sMotionSmoothingRequiredVersion: string;
}

export enum ESceneApplicationState
{
	None = 0,
	Starting = 1,
	Quitting = 2,
	Running = 3,
	Waiting = 4,
}

export enum EVRMessageOverlayResponse
{
	ButtonPress_0 = 0,
	ButtonPress_1 = 1,
	ButtonPress_2 = 2,
	ButtonPress_3 = 3,
	CouldntFindSystemOverlay = 4,            // Used by vrdashboard
	CouldntFindOrCreateClientOverlay = 5,    // Used by vrdashboard
	ApplicationQuit = 6,
}

export interface NotificationToastData
{
	sTitle: string;
	sHeader?: string;
	sText: string;
	sImageData?: string;
}

export enum EKeyboardTextInputMode
{
	Normal = 0,
	Password = 1,
	Submit = 2,
}

// Controls number of allowed lines for the Big Picture gamepad text entry
export enum EKeyboardTextInputLineMode
{
	SingleLine = 0,
	MultipleLines = 1,
}

export interface KeyboardInputInfo
{
	inputMode: EKeyboardTextInputMode;
	lineMode: EKeyboardTextInputLineMode;
	charMax: number;
	keyboardFlags: number;
	keyboardBuffer: string;
}

export enum ESystemBehaviorFlags
{
	LaserMouse						= 0x0001, // Laser mouse is active (aka the dashboard is up) and SteamVR itself keeps all input.
	Keyboard						= 0x0002, // The keyboard is visible and is currently using all input. This happens when the keyboard is up over an app.
	RoomView						= 0x0004, // Room view is visible and using all input.
	DisplaySuppressed				= 0x0008, // The driver has suppressed output to the display
	SystemButtonDown				= 0x0010, // The system button is down so we're stealing input to look for chords. No events are triggered by this one
	DriverIsDrawingControllers		= 0x0020, // The driver has taken input focus, so applications should stop rendering controllers or hands.
	DriverRequestsApplicationPause	= 0x0040, // The driver has requested the application to pause (e.g. because it is drawing something on top like a dashboard).
	DriverRequestsReducedRendering	= 0x0080, // The driver is rendering on top of the applications, so the application should reduce rendering resources.
}

export enum ECameraCompatibilityMode
{
	BULK_DEFAULT = 0,
	BULK_64K_DMA = 1,
	BULK_16K_DMA = 2,
	BULK_8K_DMA = 3,
	ISO_52FPS = 4,
	ISO_50FPS = 5,
	ISO_48FPS = 6,
	ISO_46FPS = 7,
	ISO_44FPS = 8,
	ISO_42FPS = 9,
	ISO_40FPS = 10,
	ISO_35FPS = 11,
	ISO_30FPS = 12,
	ISO_15FPS = 13,
	MAX_CAMERA_COMPAT_MODES,
}

export enum EOxrRuntimeProvider
{
	None = 0,
	ThisSteamVR = 1,
	AnotherSteamVR = 2,
	AnotherRuntime = 3,

	Error = -1,
}

export enum ETrackedControllerRole
{
	TrackedControllerRole_Invalid = 0,					// Invalid value for controller type
	TrackedControllerRole_LeftHand = 1,					// Tracked device associated with the left hand
	TrackedControllerRole_RightHand = 2,				// Tracked device associated with the right hand
	TrackedControllerRole_OptOut = 3,					// Tracked device is opting out of left/right hand selection
	TrackedControllerRole_Treadmill = 4,				// Tracked device is a treadmill
	TrackedControllerRole_Max = 5,
}

// these values are stored in OGS. Don't change them
// From ivrclientinternal.h
export enum EVRStartupReason
{
	Unknown = 0,
	Steam_VRButton = 1,
	Steam_StartSteamVRApp = 2,
	Watchdog_Unknown = 10,
	Watchdog_Controller = 11,
	Watchdog_Hmd = 12,
	AppLaunch_Unknown = 20,
	AppLaunch_Steam = 21,
	SteamVR_Restart = 30,
	SteamVR_VRStartup = 31,
}

export enum EKeyboardFlags
{
	Minimal		= 1 << 0, // makes the keyboard send key events immediately instead of accumulating a buffer
	Modal		= 2 << 0, // makes the keyboard take all focus and dismiss when clicking off the panel
}

export interface TextureSize
{
	unWidth: number;
	unHeight: number;
}

declare global {
	// this is the interface declaration for the C++ code in vrwebhelper_javscript.cpp,
	// the defintion should match its implementation
	//
	// All of these functions will be unavailable if this react app is loaded in an external
	// web browser. Always guard calls to VRHTML with optional chaining or checking that VRHTML is truthy.
	var VRHTML: IVRHTML | null;
}

export interface IVRHTML
{
	/** @deprecated Check for truthiness of VRHTML instead. */
	IsAvailable(): boolean;

	/** @deprecated Use CurrentEnvironment() instead. */
	Environment(): Environment;

	/** Generates a unique ID for this web helper instance */
	GetWebHelperId(): string;

	OpenSteamWorkshop( unSteamAppID: number ): void;
	HasHMD(): boolean;

	OpenURL( sURL: string ): void;
	IsWindowShown(): boolean;
	CloseWindow( bAllowHiding: boolean ): void;
	GetCurrentSceneFocusAppName(): string | null;
	GetStartingAppName(): string | null;
	SceneAppRunning(): boolean;
	DashboardControllerIsTracked(): boolean;
	RestartSteamVR( bMonitorOnly: boolean ): void;
	QuitOpenVR(): void;
	QuitSceneApp(): void;
	TurnOffVRController(): void;
	GetSteamVRLanguage(): string;
	GetSystemLocale(): string | null;
	GetHostInfo( eType: number ): string | null;

	ShowSettingsUI(): void; // Opens settings in Dashboard or desktop depending on where this is called from.
	ShowBindingUI( bOnDesktop: boolean ): void;
	ShowTrackerUI( bOnDesktop: boolean ): void;
	ShowBindingUIAppSelect(): void;
	PromptUserForDirectMode( bEnableDirectMode: boolean ): void;

	RegisterForOverlayStateEvents( fnCallback: ( bShown:boolean ) => void ): CallbackHandle;
	RegisterForIPDEvents( fnCallback: ( flNewIPD: number ) => void ): CallbackHandle;
	RegisterForSceneFocusChangedEvents( fnCallback: ( nOldPID: number, nPID: number ) => void ): CallbackHandle;
	RegisterForSceneApplicationChangedEvents( fnCallback: ( nOldPID: number, nPID: number ) => void ): CallbackHandle;
	RegisterForApplicationTransitionStartedEvents( fnCallback: ( nPID: number ) => void ): CallbackHandle;
	RegisterForApplicationTransitionAbortedEvents( fnCallback: ( nPID: number ) => void ): CallbackHandle;
	RegisterForApplicationTransitionNewAppStartedEvents( fnCallback: ( nPID: number ) => void ): CallbackHandle;
	RegisterForApplicationTransitionNewAppLaunchCompleteEvents( fnCallback: ( nPID: number ) => void ): CallbackHandle;
	RegisterForApplicationSecondaryRenderingStartedEvents( fnCallback: ( nPID: number ) => void ): CallbackHandle;
	RegisterForSceneApplicationStateChangedEvents( fnCallback: () => void ): CallbackHandle;
	RegisterForProgressIndicatorEvents( fnCallback: ( flProgress: number, sIconPath: string, sDevicePath: string, sInputPath: string, sAction: string ) => void ): CallbackHandle;
	RegisterForWindowEvents( fnCallback: ( eEvent: ESDLWindowEvent ) => void ): CallbackHandle;
	RegisterForDeviceEvents( fnCallback: ( eEvent: EDeviceEvent, sDevicePath: string, unTrackedDeviceIndex: number ) => void ): CallbackHandle;
	RegisterForModalCancel( fnCallback: () => void ): CallbackHandle;
	RegisterForBatteryStateChangedEvents( fnCallback: ( unTrackedDeviceIndex: number ) => void ): CallbackHandle;
	RegisterForDeviceRoleChangedEvents( fnCallback: () => void ): CallbackHandle;
	RegisterForDisplayModeNotSupportedEvents( fnCallback: () => void ): CallbackHandle;
	RegisterForDesktopViewUpdatingEvents( fnCallback: () => void ): CallbackHandle;
	RegisterForDesktopViewReadyEvents( fnCallback: () => void ): CallbackHandle;

	GetPose( sPathOrPropHandle: string, eTrackingOrigin: ETrackingUniverseOrigin ): TrackedDevicePose;
	GetPose( unDeviceIndex: number, eTrackingOrigin: ETrackingUniverseOrigin ): TrackedDevicePose;

	ChangeBasis( xf: TRSTransform, xfBasis: TRSTransform ): TRSTransform;
	MultiplyTransforms( xfA: TRSTransform, xfB: TRSTransform ): TRSTransform;

	AddSystemBehaviorFlag( flag: ESystemBehaviorFlags ): void;
	RemoveSystemBehaviorFlag( flag: ESystemBehaviorFlags ): void;

	// You probably don't want to call this
	CrashRenderer(): void;

	SteamVRVersion(): string;
	GetVRStartupReason(): EVRStartupReason;

	GetRecommendedOverlayRenderQuality(): EOverlayRenderQuality;

	VRPathRegistry: {
		GetRuntimePath(): string;
		SetRuntimePath( sPath: string ): void;
		GetRuntimePathHistory(): [string];
	};

	VRRenderModels: {
		GetComponentStateForDevicePath( sRenderModelName: string, sComponentName: string, sDevicePath: string ): RenderModel_ComponentState_t;
	};

	VRRenderModelsInternal: {
		FindComponentForInputSource( sRenderModelName: string, sInputSourcePath: string ): string;
	};

	VRProperties: {
		GetStringProperty( sPathOrPropHandle: string, eProperty: ETrackedDeviceProperty ): string;
		GetStringProperty( unDeviceIndex: number, eProperty: ETrackedDeviceProperty ): string;
		GetFloatProperty( sPathOrPropHandle: string, eProperty: ETrackedDeviceProperty ): number;
		GetFloatProperty( unDeviceIndex: number, eProperty: ETrackedDeviceProperty ): number;
		GetBoolProperty( sPathOrPropHandle: string, eProperty: ETrackedDeviceProperty ): boolean;
		GetBoolProperty( unDeviceIndex: number, eProperty: ETrackedDeviceProperty ): boolean;
		GetInt32Property( sPathOrPropHandle: string, eProperty: ETrackedDeviceProperty ): number;
		GetInt32Property( unDeviceIndex: number, eProperty: ETrackedDeviceProperty ): number;
		SetInt32Path( sRootPath: string, nValue: number ): boolean;
	};

	VRSettings: {
		Get( sSection: string, sKey: string ): boolean|number|string;
		Set( sSection: string, sKey: string, value?: boolean|number|string ): void;
	};

	VRCompositor: {
		GetTrackingSpace(): number;
		RegisterForTrackingSpaceChangedEvents( fnCallback: () => void ): CallbackHandle;
		RegisterForRoomViewChangedEvents( fnCallback: () => void ): CallbackHandle;
	};

	VRChaperone: {
		ForceBoundsVisible( bShowBounds: boolean ): void;
		ResetZeroPose( eTrackingUniverseOrigin: ETrackingUniverseOrigin ): void;
	};

	VRChaperoneSetup: {
		SetWorkingPlayAreaSize( fSizeX: number, fSizeY: number ): void;
		SetWorkingStandingZeroPoseToRawTrackingPose( xfStandingToRaw: TRSTransform ): void;
		SetWorkingPerimeter( rPoints: Vector2[] ): void;
		ShowWorkingSetPreview(): void;
		HideWorkingSetPreview(): void;
		CommitWorkingCopy(): boolean;
		RevertWorkingCopy(): void;
		GetLiveCollisionBoundsInfo(): Quad[];
	};

	VROverlay: {
		GetPrimaryDashboardDevice(): number;
		Hide(): void;
		Show(): void;
		ShowKeyboard( sDescription: string, sInitialText: string, nMaxChars: number,
			fnCallback: ( sEnteredText:string ) => void ): CallbackHandle;
		ShowKeyboardForOverlay( sOverlayKey: string, eTextInputMode: EKeyboardTextInputMode,
			eLineInputMode: EKeyboardTextInputLineMode,	unKeyboardFlags: number, sDescription: string,
			unCharMax: number, sExistingText: string, unUserValue: number ): void;
		SetKeyboardPositionForOverlay( sOverlayKey: string, avoidRect: any ): string;
		HideKeyboard(): void;
		FindOverlay( sOverlayKey: string ): string;
		ThisOverlayHandle(): string;
		ThisOverlayKey(): string;
		GetFlag( VROverlayHandle_t: string, eFlag: EVROverlayFlags ): boolean;
		SetFlag( VROverlayHandle_t: string, eFlag: EVROverlayFlags, bValue: boolean ): void;
		GetOverlayAlpha( VROverlayHandle_t: string ): number;
		SetOverlayAlpha( VROverlayHandle_t: string, fAlpha: number ): void;
		GetOverlayTextureSize( VROverlayHandle_t: string ): TextureSize;
		GetWidthInMeters( VROverlayHandle_t: string ): number;
		SetWidthInMeters( VROverlayHandle_t: string, fNewWidth: number ): void;
		GetInputMethod( VROverlayHandle_t: string ): number;
		SetInputMethod( VROverlayHandle_t: string, eInputMethod: EVROverlayInputMethod ): void;
		ShowDashboard( sOverlayKey: string ): void;
		TriggerLaserMouseHapticVibration( VROverlayHandle_t: string, fDuration: number, fFrequency: number, fAmplitude: number ): void;
	};

	VRSystem: {
		GetTrackedDeviceClasses(): TrackedDeviceInfo[];
		IsDevicePathValid( sDevicePath: string ): boolean;
		GetControllerRoleForTrackedDeviceIndex( unDeviceIndex: number ): ETrackedControllerRole;
		GetTrackedDeviceIndexForControllerRole( eRole: ETrackedControllerRole ): number;
	};

	VRControlPanel: {
		GetCameraRoomViewVisible(): boolean;
		SetCameraRoomViewVisible( bShowRoomView: boolean ): void;
	};

	VRDashboardManager: {
		GetOverlayImage( sOverlayKey: string ): string;
		HideDashboard( sReason: string ): void;
		OnMessageOverlayResponse( response: EVRMessageOverlayResponse ): void;
		HasMessageOverlay(): boolean;
		ShouldShowTrackerUI(): boolean;
		SetKeyboardOverlayToThis(): void;
		GetKeyboardInputInfo(): KeyboardInputInfo;
		SendKeyboardDone(): void;
		CloseKeyboard(): void;
		GetNightModeEnabled(): boolean;
		SetNightModeEnabled( bIsEnabled: boolean ): void;

	};

	VROverlayInternal:
	{
		HasCameraRoomViewCapability(): boolean;
		GetToastInfo( nNotificationId: number ): NotificationToastData;
	};

	VRApplications: {
		GetCurrentSceneProcessId(): number;
		GetSceneApplicationKey(): string | null;
		GetStartingApplicationKey(): string | null;
		LaunchApplication( sKey: string ): void;
		GetSceneApplicationState(): ESceneApplicationState
	};

	VRAudioDevices: {
		GetMasterSoundVolume(): number;
		IsMasterSoundVolumeMute(): boolean;
		SetMasterSoundVolume( fVolume: number ): void;
		SetMasterSoundVolumeMute( bMuted: boolean ): void;
		GetMirrorSoundVolume(): number;
		IsMirrorSoundVolumeMute(): boolean;
		SetMirrorSoundVolume( fVolume: number ): void;
		SetMirrorSoundVolumeMute( bMuted: boolean ): void;
		GetMasterMicrophoneVolume(): number;
		IsMasterMicrophoneMute(): boolean;
		SetMasterMicrophoneVolume( fVolume: number ): void;
		SetMasterMicrophoneMute( bMuted: boolean ): void;
		RegisterForVolumeChangedEvents( fnCallback: () => void ): CallbackHandle;
		RegisterForDeviceChangedEvents( fnCallback: () => void ): CallbackHandle;
	};

	VRMonitor: {
		ShowBluetoothSettings(): void;
		ShowBasestationSettings(): void;
	};

	MonitorController: {
		GetWakingBaseStationsState(): boolean;
	};

	VRKeyboard: {
		RegisterForGetKeyboardText( fnCallbackup: () => void ): CallbackHandle;
		SetKeyboardText( text: string ): void;
		KeyboardDone(): void;
		ReportKeyboardStats( unTypedThumbpadCount: number, unTypedPointerCount: number,
			unTypedGamepadCount: number, unOutputCount: number, unDeleteCount: number ): void;
		LoadSuggestionsForLanguage( language: string ): void;
		GetSuggestions( str: string, maxCandidates: number ): string[];
		StartHapticFeedback( device: number ): void;
	};

	VRTrackedCameraInternal: {
		SetCameraCompatibilityMode( eNewMode: ECameraCompatibilityMode ): boolean;
		StartCameraTest(): boolean;
		StopCameraTest(): void;
		GetCameraTestFrame(): string;
	};

	VRClientInternal: {
		GetGraphicsHardwareVendorInfo(): GraphicsHardwareVendorInfo;
	};

	XRRuntimeManager: {
		GetCurrentRuntime(): [ EOxrRuntimeProvider, string ];
		IsThisRuntimeCurrent(): boolean;
		SetThisRuntimeToCurrent(): void;
	};
}

